import CertificationStatus, {
  CertificationStatusProps,
} from "./CertificationStatus";
import React, { useEffect } from "react";
import confetti from "canvas-confetti";
import {
  Box,
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Text,
  useBreakpointValue,
  useColorModeValue,
  useTheme,
} from "@chakra-ui/react";
import { CONFETTI_CONFIG } from "@utils";
import { FaTwitter } from "@react-icons/all-files/fa/FaTwitter";
import { Link as GatsbyLink, graphql, useStaticQuery } from "gatsby";
import { TwitterShareButton } from "react-share";

type CourseCompletionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string | null;
  courseSlug: string;
} & Pick<Queries.Course, "progressId"> &
  Pick<CertificationStatusProps, "user">;

export default function CourseCompletionModal({
  isOpen,
  onClose,
  title,
  courseSlug,
  progressId,
  user,
}: CourseCompletionModalProps) {
  const { zIndices } = useTheme();

  const { site, allCertification } =
    useStaticQuery<Queries.ListCertificationsAndSiteUrlQuery>(
      graphql`
        query ListCertificationsAndSiteUrl {
          site {
            pathPrefix
            siteMetadata {
              siteUrl
            }
          }
          allCertification {
            nodes {
              progressId
              title
              criteria
              image
              courses {
                id
                slug
                progressId
              }
            }
          }
        }
      `
    );

  const certification = allCertification.nodes
    .map(({ progressId, ...certification }) => ({
      id: progressId,
      ...certification,
    }))
    .find(({ criteria }) => criteria.includes(progressId));

  useEffect(() => {
    if (isOpen && !certification) {
      confetti({
        ...CONFETTI_CONFIG,
        spread: 360,
        particleCount: 100,
        zIndex: zIndices.popover,
        disableForReducedMotion: true,
      });
    }
  }, [isOpen, zIndices, certification]);

  const modalSize = useBreakpointValue(["sm", "lg", "3xl", "5xl"]);
  const closeButtonColor = useColorModeValue("white", "current");

  const siteUrl = `${site?.siteMetadata?.siteUrl}${site?.pathPrefix}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={certification ? modalSize : undefined}
    >
      <ModalOverlay />
      <ModalContent aria-label="Course completed!">
        <ModalCloseButton
          // put close button on top of confetti canvas for mobile layout
          zIndex="1"
          color={{
            base: closeButtonColor,
            md: "current",
          }}
        />
        <SimpleGrid
          columns={certification ? { md: 2 } : 1}
          minH={certification ? "522px" : 0}
        >
          {certification && (
            <CertificationStatus user={user} certification={certification} />
          )}
          <ModalBody
            display="flex"
            flexDir="column"
            p={certification ? { base: 6, md: 8 } : 6}
          >
            <Stack spacing="4" fontSize="lg" mb="8">
              <Heading size="lg">
                You did it{" "}
                <span role="img" aria-label="party popper">
                  🎉
                </span>
              </Heading>
              <Text>
                Hooray! You’ve completed <strong>{title}</strong>!
              </Text>
              <Text>
                Let the world know about your achievement by sharing with your
                colleagues and fellow developers.
              </Text>
              <div>
                <Button
                  resetButtonStyle={false}
                  url={`${siteUrl}/${courseSlug}`}
                  title={
                    "I've just completed a course on Odyssey, @apollographql's learning platform! Check it out if you want to become a GraphQL expert:"
                  }
                  as={TwitterShareButton}
                  colorScheme="twitter"
                  leftIcon={<FaTwitter />}
                  onClick={() => window.gtag?.("event", "tweet_button_click")}
                >
                  Tweet
                </Button>
              </div>
              <Text>
                Thank you for learning with us. Until next time!{" "}
                <span role="img" aria-label="waving hand">
                  👋
                </span>
              </Text>
            </Stack>
            <Box mt="auto" textAlign="right">
              <Button
                as={GatsbyLink}
                to="/dashboard/courses"
                onClick={() => window.gtag?.("event", "dashboard_button_click")}
              >
                View dashboard
              </Button>
            </Box>
          </ModalBody>
        </SimpleGrid>
      </ModalContent>
    </Modal>
  );
}
